import React, { useState, useEffect } from "react";
import { Modal, Typography, Button, Checkbox, Select, Radio, Input } from "antd";
import type { CheckboxChangeEvent } from "antd/es/checkbox";
import { gxpAuditReasons as deletionReasons } from "const";

const { Title, Paragraph } = Typography;
const { Option } = Select;


type ConfirmDeleteModalProps = {
  visible: boolean;
  onConfirm: (hardDelete: boolean, deletionReason: { value: string, label: string }, delRepo: boolean) => void;
  onCancel: () => void;
  spaceType?: string; // GxP flag to determine if space is Non-GxP
  gitRepoLink?: string; 
};

const ConfirmDeleteModal: React.FC<ConfirmDeleteModalProps> = ({
  visible,
  onConfirm,
  onCancel,
  spaceType,
  gitRepoLink,
}) => {
  const [hardDelete, setHardDelete] = useState(false);
  const [deletionReason, setDeletionReason] = useState<{ value: string, label: string } | null>(null);
  const [customReason, setCustomReason] = useState("");
  const [gitRepoDel, setGitRepoDel] = useState(false);

  // Check if this is a Non-GxP space
  const isNonGxpSpace = spaceType?.toLowerCase() === "nongxp" || spaceType?.toLowerCase() === "non-gxp";
  //controls whether hard delete is always enforced or the user can choose soft vs hard delete.
  // =========make this variable false in non-prod, qa and prod environments ==========
  // When true, hard delete is forced; when false, the user can choose between soft and hard delete (where allowed).
  const forceHardDelete = true; // Change to false to show soft/hard delete options

  const showCustomReasonInput = true; // Set to true to show the custom reason input when an "Other (specify)" deletion reason is selected
  // Check if "Other (specify)" is selected
  
  const isOtherReason = deletionReason?.value?.endsWith("Other-specify") || false;

  useEffect(() => {
    if (!visible) {
      setHardDelete(false);
      setDeletionReason(null);
      setCustomReason("");
      setGitRepoDel(false);
    }
  }, [visible]);

  const handleCheckboxChange = (e: CheckboxChangeEvent) => {
    setHardDelete(e.target.checked);
  };

  const handleReasonChange = (value: string) => {
    for (const group of deletionReasons) {
      const option = group.options.find(opt => opt.value === value);
      if (option) {
        setDeletionReason({ value: option.value, label: option.label });
        // Reset custom reason when switching to different option
        setCustomReason("");
        return;
      }
    }
    setDeletionReason(null);
    setCustomReason("");
  };

  const handleCustomReasonChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCustomReason(e.target.value);
  };
  const handleConfirm = () => {
    // For Non-GxP spaces, we don't require a deletion reason
    if (isNonGxpSpace || (deletionReason && (!isOtherReason || !showCustomReasonInput || (customReason.trim() && customReason.trim().length >= 10)))) {
      // For Non-GxP spaces, use default values that won't be sent to API
      let reasonToUse = isNonGxpSpace
        ? { value: "not-applicable", label: "Not applicable for Non-GxP spaces" }
        : deletionReason;
      
      // If "Other (specify)" is selected, use the custom reason
      if (isOtherReason && customReason.trim() && deletionReason) {
        reasonToUse = {
          value: `${deletionReason.value}: ${customReason.trim()}`,
          label: `${deletionReason.label}: ${customReason.trim()}`,
        };
      }
      
      // For GxP spaces: if forceHardDelete is true, always hard delete
      // If forceHardDelete is false, use the checkbox value
      const hardDeleteToUse = isNonGxpSpace ? false : (forceHardDelete ? true : hardDelete);

      if (reasonToUse) {
        onConfirm(hardDeleteToUse, reasonToUse, gitRepoDel);
      }
    }
    setHardDelete(false);
    setDeletionReason(null);
    setCustomReason("");
  };

  const handleCancel = () => {
    onCancel();
    setHardDelete(false);
    setDeletionReason(null);
    setCustomReason("");
  };  

  return (
    <Modal
      open={visible}
      title={<Title level={4}>Nexus S3 Explorer : Confirm Space Deletion</Title>}
      onCancel={handleCancel}
      footer={[
        <Button key="no" onClick={handleCancel}>
          No
        </Button>,
        <Button
          key="yes" type="primary" danger onClick={handleConfirm}
          disabled={!isNonGxpSpace && (!deletionReason || (isOtherReason && showCustomReasonInput && (!customReason.trim() || customReason.trim().length < 10)))} // For Non-GxP spaces, don't require reason selection
        >
          Yes
        </Button>,
      ]}
      centered
      destroyOnClose
    >
      <Paragraph>
        Nexus S3 Explorer is going to delete the Space. With deletion of this space all related annotation information,
        tables and analytics environments will be removed as well.
      </Paragraph>
      {!isNonGxpSpace && forceHardDelete && (
        <Paragraph>
          <strong style={{ color: "#ff4d4f" }}>
            Warning: This action is irreversible and will permanently delete the space.
          </strong>
        </Paragraph>
      )}
      <Paragraph>
      </Paragraph>
	  Delete The corresponding Git Repo ?
		<br/>
    {!gitRepoLink && (
      <Paragraph style={{ color: "#faad14", fontSize: "12px", marginTop: "4px", marginBottom: "8px" }}>
        <strong>Note:</strong> No Git repository is associated with this space. This option is disabled.
      </Paragraph>
    )}
	  <Radio.Group
			value={gitRepoDel}
			onChange={(e) => {
				setGitRepoDel(e.target.value);
			}}
      disabled={!gitRepoLink}
		>
			<Radio value={ true }>Yes</Radio>
			<Radio
				value={ false }
			>
				No
			</Radio>
		</Radio.Group>

      {/* Only show reason selection for GxP spaces */}
      {!isNonGxpSpace && (
        <>
          <Select
            placeholder="Please select a reason for deletion"
            style={{ width: "100%", marginBottom: 16 }}
            onChange={handleReasonChange}
            value={deletionReason?.value || undefined}
          >
            {deletionReasons.map(group => (
              <Select.OptGroup key={group.group} label={group.group}>
                {group.options.map(option => (
                  <Select.Option key={option.value} value={option.value}>
                    {option.label}
                  </Select.Option>
                ))}
              </Select.OptGroup>
            ))}
          </Select>
          
          {/* Show custom reason input when "Other (specify)" is selected AND showCustomReasonInput is true */}
          {isOtherReason && showCustomReasonInput && (
            <Input
              placeholder="Please specify the reason (minimum 10 characters)"
              value={customReason}
              onChange={handleCustomReasonChange}
              style={{ width: "100%", marginBottom: 16 }}
              minLength={10}
              maxLength={200}
            />
          )}
        </>
      )}
      
      {/* Only show hard delete checkbox for GxP spaces when ENABLE_HARD_DELETE is false */}
      {!isNonGxpSpace && !forceHardDelete && (
        <>
          <Paragraph strong style={{ marginTop: 16, marginBottom: 8 }}>Accept to proceed?</Paragraph>
          <Checkbox checked={hardDelete} onChange={handleCheckboxChange}>
            Perform permanent delete (irreversible)
          </Checkbox>
        </>
      )}
    </Modal>
  );
};

export default ConfirmDeleteModal;
